import { DynamicFormFieldTypes } from '@common/types';
import dayjs from 'dayjs';
import { Parser } from 'expr-eval-fork';
import * as yup from 'yup';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  MAXIMUM_TEXT_AREA_LENGTH,
  MAXIMUM_ARRAY_LENGTH,
  MAXIMUM_STRING_LENGTH,
  MAXIMUM_TEXT_LENGTH,
} from '@common/types/constants.ts';

export const mbiRegex =
  /^[0-9][ACDEFGHJKMNPQRTUVWXY][ACDEFGHJKMNPQRTUVWXY0-9][0-9]-?[ACDEFGHJKMNPQRTUVWXY][ACDEFGHJKMNPQRTUVWXY0-9][0-9]-?[ACDEFGHJKMNPQRTUVWXY]{2}[0-9]{2}$/;
const idRegex = /^[A-Za-z0-9!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/;
export const textRegex = /^[\w\s\-.,]*$/;
export const zipRegex = /^\d{2,10}(?:[-\s]\d{4})?$/;
export const stateRegex =
  /^(|AL|AK|AZ|AR|CA|CO|CT|DE|DC|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY)$/;

export const internationalStateRegex = /^[A-Z]{2}$/;

export const requiredErrorMessage = 'Field is required';
export const nameRegex =
  /^[\p{L}\p{M}\d.,'’\-·&+/#()@%:!$*_[\]|=<>?~`]+(?: [\p{L}\p{M}\d.,'’\-·&+/#()@%:!$*_[\]|=<>?~`]+)*$/u;
export const domainRegex = /^$|^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const dementiaDiagnosisRegex = /^[A-Za-z0-9.]+$/;

export const addRequiredToSchema = (
  schema: yup.Schema,
  type: DynamicFormFieldTypes,
) => {
  if (type === 'checkbox') {
    return schema.oneOf(['true', 'false'], requiredErrorMessage);
  }

  if (type === 'repeater_textfield' || type === 'repeater_textarea') {
    return schema.test('required-repeater', 'No empty entries.', value => {
      const arr = parseStringArray(value).map(s => String(s ?? '').trim());
      return arr.length > 0 && arr.every(s => s.length > 0);
    });
  }

  return schema.required(requiredErrorMessage);
};

export const addDisableConditionToSchema = ({
  schema,
  disable_if,
}: {
  schema: yup.Schema;
  disable_if: string;
}) => {
  const parser = new Parser();

  const expr = parser.parse(disable_if);

  return schema.when([], (fields, schemaObject, { context }) =>
    !expr.evaluate(context?.values)
      ? schemaObject
      : yup
          .mixed()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value,
          )
          .optional()
          .nullable(),
  );
};

export const disabledSchema = yup
  .mixed()
  .transform((value, originalValue) =>
    originalValue === '' ? undefined : value,
  )
  .optional()
  .nullable();

export const addOptionalToSchema = <TType, TContext, TOut>(
  schema: yup.Schema<TType, TContext, TOut>,
): yup.Schema<TType | undefined, TContext, TOut | undefined> => {
  return schema
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value,
    )
    .optional();
};

export const addOptionalNullableToSchema = <TType, TContext, TOut>(
  schema: yup.Schema<TType, TContext, TOut>,
): yup.Schema<TType | null | undefined, TContext, TOut | null | undefined> => {
  return schema
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable()
    .optional();
};

export const radioButtonSchema = yup.string();
export const selectSchema = yup.string();
export const checkboxSchema = yup.string();
export const googlePlacesSchema = yup.string();

const parseStringArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(x => String(x ?? ''));
  if (typeof v !== 'string' || v.trim() === '') return [];
  try {
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed.map(x => String(x ?? '')) : [];
  } catch {
    return [];
  }
};

export const repeaterTextFieldSchema = yup
  .string()
  .test('json-array', 'Invalid list format.', value => {
    if (!value) return true;
    return Array.isArray(parseStringArray(value));
  })
  .test(
    'item-max-length',
    `Each item must be at most ${MAXIMUM_STRING_LENGTH} characters.`,
    value => {
      const arr = parseStringArray(value);
      return arr.every(s => s.trim().length <= MAXIMUM_STRING_LENGTH);
    },
  )
  .test(
    'item-count',
    `There can be at most ${MAXIMUM_ARRAY_LENGTH} items.`,
    value => {
      const arr = parseStringArray(value);
      return arr.length < MAXIMUM_ARRAY_LENGTH;
    },
  );

export const repeaterTextareaSchema = yup
  .string()
  .test('json-array', 'Invalid list format.', value => {
    if (!value) return true;
    return Array.isArray(parseStringArray(value));
  })
  .test(
    'item-max-length',
    `Each item must be at most ${MAXIMUM_TEXT_AREA_LENGTH} characters.`,
    value => {
      const arr = parseStringArray(value);
      return arr.every(s => s.trim().length <= MAXIMUM_TEXT_AREA_LENGTH);
    },
  )
  .test(
    'item-count',
    `There can be at most ${MAXIMUM_ARRAY_LENGTH} items.`,
    value => {
      const arr = parseStringArray(value);
      return arr.length < MAXIMUM_ARRAY_LENGTH;
    },
  );

export const domainSchema = yup
  .string()
  .matches(domainRegex, { message: 'Please enter a valid domain' });

export const nameSchema = yup
  .string()
  .matches(nameRegex, 'Please enter only letters, numbers, and single spaces')
  .max(MAXIMUM_STRING_LENGTH);

export const requiredNameSchema = yup
  .string()
  .required(requiredErrorMessage)
  .matches(nameRegex, 'Please enter only letters, numbers, and single spaces')
  .max(MAXIMUM_STRING_LENGTH);

export const optionalNameSchema = nameSchema
  .transform((value, originalValue) =>
    originalValue === '' ? undefined : value,
  )
  .optional();

export const optionalNullableNameSchema = nameSchema
  .transform((value, originalValue) => (originalValue === '' ? null : value))
  .nullable()
  .optional();

export const strongPasswordSchema = yup
  .string()
  .test(
    'is-strong-password',
    'Password must be at least 12 characters long and include uppercase, lowercase, number, and symbol.',
    value => {
      if (!value) return false;
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /[0-9]/.test(value);
      const hasSymbol = /[^A-Za-z0-9]/.test(value);
      const isLongEnough = value.length >= 12;

      return (
        hasUpperCase && hasLowerCase && hasNumber && hasSymbol && isLongEnough
      );
    },
  )
  .required(requiredErrorMessage);

export const streetSchema = nameSchema;
export const suiteSchema = nameSchema;
export const citySchema = nameSchema;
export const stateSchema = yup
  .string()
  .matches(stateRegex, { message: 'Valid state format: CA, NY, FL etc.' })
  .max(2, 'Maximum of 2 digits allowed');

export const internationalStateSchema = yup
  .string()
  .transform(v => (v == null || v === '' ? undefined : v))
  .matches(internationalStateRegex, {
    message: 'Valid state format: CA, NY, FL etc.',
  })
  .max(2, 'Maximum of 2 digits allowed')
  .notRequired()
  .nonNullable();

export const countySchema = nameSchema;
export const countrySchema = nameSchema;
export const textSchema = yup.string().max(MAXIMUM_STRING_LENGTH);
export const textAreaSchema = yup.string().max(MAXIMUM_TEXT_LENGTH);
export const simpleStringSchema = yup.string();

export const zipSchema = yup
  .string()
  .max(10)
  .matches(zipRegex, { message: 'Please enter a valid zip code' });

export const phoneSchema = yup
  .string()
  .test('is-valid-phone', 'Must match a valid phone number format', value => {
    if (!value) return true;
    return isValidPhoneNumber(value, 'US');
  })
  .optional();

export const emailSchema = yup
  .string()
  .optional()
  .matches(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'Please enter a valid email')
  .email('Please enter a valid email');

export const mbiSchema = yup
  .string()
  .matches(mbiRegex, {
    message: 'Must match a valid MBI',
    excludeEmptyString: true,
  })
  .transform((value: string) => (value ? value.replace(/-/g, '') : value))
  .max(11, 'Maximum of 11 alphanumerical characters allowed');

export const idSchema = yup
  .string()
  .optional()
  .matches(idRegex, 'Must only contain printable characters')
  .max(
    MAXIMUM_STRING_LENGTH,
    `Maximum of ${MAXIMUM_STRING_LENGTH} characters allowed`,
  );

export const providerNPISchema = yup
  .string()
  .matches(/^\d{10}$/, 'Must be a 10 digit number');

export const dateSchema = yup.string().optional().defined().default(undefined);

export const pastDateSchema = yup
  .string()
  .optional()
  .defined()
  .default(undefined)
  .test('date-in-past', function (value) {
    const isInPast = dayjs(value).isBefore(dayjs());
    if (value && !isInPast) {
      return this.createError({
        path: this.path,
        message: 'Date must be in the past',
      });
    }

    return true;
  });

export const pastOrTodayDateSchema = yup
  .string()
  .optional()
  .defined()
  .default(undefined)
  .test('date-in-past-or-today', function (value) {
    if (!value) return true;

    const date = dayjs(value);
    if (!date.isValid()) {
      return this.createError({
        path: this.path,
        message: 'Invalid date',
      });
    }

    const isAfterToday = date.isAfter(dayjs(), 'day');

    if (isAfterToday) {
      return this.createError({
        path: this.path,
        message: 'Date must be today or in the past',
      });
    }

    return true;
  });

export const todayOrFutureDateSchema = yup
  .string()
  .optional()
  .defined()
  .default(undefined)
  .test('date-today-or-future', function (value) {
    if (!value) return true;

    const date = dayjs(value);
    if (!date.isValid()) {
      return this.createError({
        path: this.path,
        message: 'Invalid date',
      });
    }

    if (date.isBefore(dayjs().startOf('day'))) {
      return this.createError({
        path: this.path,
        message: 'Date cannot be in the past',
      });
    }

    return true;
  });

export const searchSchema = yup.string();

export const repeaterSchema = <TType, TContext extends yup.AnyObject, TDefault>(
  itemSchema: yup.Schema<TType, TContext, TDefault, ''>,
  options?: {
    minNumberOfEntries?: number;
    maxNumberOfEntries?: number;
  },
) => {
  const { minNumberOfEntries, maxNumberOfEntries } = options ?? {};

  let schema = yup.array().of(itemSchema);

  if (typeof minNumberOfEntries === 'number') {
    schema = schema.min(
      minNumberOfEntries,
      `At least ${minNumberOfEntries} item(s) are required`,
    );
  }

  if (typeof maxNumberOfEntries === 'number') {
    schema = schema.max(
      maxNumberOfEntries,
      `At most ${maxNumberOfEntries} item(s) are allowed`,
    );
  }

  return schema;
};
